import React, { useContext } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axiosAdmin from "../../../../api/apiAdmin";
import { Link } from "react-router-dom";
import { AdminPrimaryContext } from "../../adminContext/AdminContext";
import { CheckBox, SelectInputValueId } from "../../inputComponents/inputcomp";
import { BsX } from "react-icons/bs";
import { successToast, errorToast } from "../../toastAlerts/ToastAlerts";
import { apiError } from "../../../../api/apiError";
import AdminDownloadLink from "../../AdminDownloadLink";

const ExportsPage = () => {
  const { t } = useTranslation();
  const { foreignAdresses, geBranches } = useContext(AdminPrimaryContext);
  const [filterData, setFilterData] = useState({
    abOffices: [],
    shipments: [],
  });

  const [selectedFilters, setSelectedFilterValues] = useState({
    abOfficeId: null,
    shipmentId: null,
    transport: null,
    status: null,
    office_id: null,
  });

  // A date range on ONE chosen moment of the parcel's life (Pack::DATE_KINDS):
  // received abroad, sent, arrived, shown, collected, paid.
  const [dateKind, setDateKind] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const dateActive = dateKind && (dateFrom || dateTo);

  // How many parcels the current filters match — a date range is easy to get
  // wrong by a day, and the only other feedback was the size of the download.
  const [matchCount, setMatchCount] = useState(null);

  // Users ledger: optional single user, and the page's date range applies to
  // the movements sheet. Downloaded through axios because the route is
  // admin-guarded — a plain link carries no token.
  const [ledgerUser, setLedgerUser] = useState("");
  const [ledgerBusy, setLedgerBusy] = useState(false);
  const downloadLedger = async () => {
    setLedgerBusy(true);
    try {
      const q = new URLSearchParams();
      if (ledgerUser.trim()) q.set("user", ledgerUser.trim());
      if (dateFrom) q.set("date_from", dateFrom);
      if (dateTo) q.set("date_to", dateTo);
      const res = await axiosAdmin.get(`/exportUsersLedger?${q.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.setAttribute("download", `users-bog-deposits${ledgerUser.trim() ? `-${ledgerUser.trim()}` : ""}.xlsx`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      errorToast(apiError(err, t("error")));
    } finally {
      setLedgerBusy(false);
    }
  };

  const handleSelectedFilters = (value, key) => {
    setSelectedFilterValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleNullifySelectId = (key) => {
    setSelectedFilterValues((prev) => ({
      ...prev,
      [key]: null,
    }));
  };

  useEffect(() => {
    setFilterData((prev) => ({
      ...prev,
      abOffices: foreignAdresses.map((item) => {
        return {
          value: item.id,
          name: item.country,
        };
      }),
    }));
  }, [foreignAdresses]);

  useEffect(() => {
    axiosAdmin
      .get(`/shipments?per_page=500`)
      .then(({ data }) => {
        setFilterData((prev) => ({
          ...prev,
          shipments: data.data.map((item) => {
            return {
              value: item.code,
              name: item.code,
            };
          }),
        }));
      })
      .catch((err) => {});
  }, []);

  /**
   * The filter query the export endpoints take.
   *
   * `repack` keeps its quirk: the select uses the string "false" for "any",
   * which the backend reads as absent — passing it through would filter on the
   * literal string instead.
   */
  const exportQuery = () => {
    const p = new URLSearchParams();
    p.set("status", selectedFilters.status !== null ? selectedFilters.status : "");
    if (selectedFilters.abOfficeId !== null)
      p.set("abroad_office_id", selectedFilters.abOfficeId);
    if (selectedFilters.shipmentId !== null)
      p.set("shipment_code", selectedFilters.shipmentId);
    if (selectedFilters.transport !== null)
      p.set("repack", selectedFilters.transport === "false" ? "" : selectedFilters.transport);
    if (selectedFilters.office_id !== null)
      p.set("office_id", selectedFilters.office_id);
    if (dateActive) {
      p.set("date_kind", dateKind);
      if (dateFrom) p.set("date_from", dateFrom);
      if (dateTo) p.set("date_to", dateTo);
    }

    return p.toString();
  };

  useEffect(() => {
    const q = new URLSearchParams(exportQuery());
    q.set("per_page", "1");
    setMatchCount(null);
    axiosAdmin
      .get(`/packs?${q.toString()}`)
      .then(({ data }) => setMatchCount(data?.meta?.total ?? null))
      .catch(() => setMatchCount(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters, dateKind, dateFrom, dateTo]);

  return (
    <div
      className="xl:ml-[280px] mt-[55px] bg-slate-100 dark:bg-slate-900 
        min-h-screen p-10 sm:p-5 pb-24 sm:pb-24"
    >
      <div className="grid grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-5">
        <SelectInputValueId
          name={t("admin_abroad_office")}
          options={filterData.abOffices}
          inputName={"abOfficeId"}
          inputSetter={handleSelectedFilters}
          value={selectedFilters.abOfficeId}
          needClear={true}
        />
        <SelectInputValueId
          name={t("admin_flights_label")}
          options={filterData.shipments}
          inputName={"shipmentId"}
          inputSetter={handleSelectedFilters}
          value={selectedFilters.shipmentId}
          needClear={true}
        />
        <SelectInputValueId
          name={t("admin_status")}
          options={[
            {
              name: t("admin_in_warehouse"),
              value: 1,
            },
            {
              name: t("admin_sent_status"),
              value: 2,
            },
            {
              name: t("admin_arrived_status"),
              value: 3,
            },
            {
              name: t("admin_delivered_status"),
              value: 4,
            },
          ]}
          inputName={"status"}
          inputSetter={handleSelectedFilters}
          value={selectedFilters.status}
          needClear={true}
        />
        <SelectInputValueId
          name={t("admin_georgia_offices")}
          options={geBranches}
          inputName={"office_id"}
          inputSetter={handleSelectedFilters}
          value={selectedFilters.office_id}
          needClear={true}
        />
      </div>
      <div className="mt-5 rounded-xl bg-white dark:bg-slate-700 p-4 flex flex-wrap items-end gap-4">
        <label className="flex flex-col gap-1 text-xs text-[#777a83] dark:text-slate-300">
          {t("admin_date_kind")}
          <select
            className="h-9 px-2 border border-slate-300 dark:border-slate-600 rounded-md
                       bg-white dark:bg-slate-800 text-slate-800 dark:text-white text-sm"
            value={dateKind}
            onChange={(e) => setDateKind(e.target.value)}
          >
            <option value="">—</option>
            <option value="received">{t("admin_date_received")}</option>
            <option value="sent">{t("admin_date_sent")}</option>
            <option value="arrived">{t("admin_date_arrived")}</option>
            <option value="shown">{t("admin_date_shown")}</option>
            <option value="delivered">{t("admin_date_delivered")}</option>
            <option value="paid">{t("admin_date_paid")}</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-[#777a83] dark:text-slate-300">
          {t("admin_date_from")}
          <input
            type="date"
            className="h-9 px-2 border border-slate-300 dark:border-slate-600 rounded-md
                       bg-white dark:bg-slate-800 text-slate-800 dark:text-white text-sm"
            value={dateFrom}
            max={dateTo || undefined}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-[#777a83] dark:text-slate-300">
          {t("admin_date_to")}
          <input
            type="date"
            className="h-9 px-2 border border-slate-300 dark:border-slate-600 rounded-md
                       bg-white dark:bg-slate-800 text-slate-800 dark:text-white text-sm"
            value={dateTo}
            min={dateFrom || undefined}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </label>
        {(dateKind || dateFrom || dateTo) && (
          <BsX
            className="text-red-500 text-2xl shrink-0 cursor-pointer mb-1.5"
            title={t("admin_clear")}
            onClick={() => {
              setDateKind("");
              setDateFrom("");
              setDateTo("");
            }}
          />
        )}
        {dateKind && !(dateFrom || dateTo) && (
          <span className="text-xs text-amber-600 mb-2">{t("admin_date_pick_range")}</span>
        )}
        <span className="ml-auto text-sm dark:text-slate-200 mb-1.5">
          {t("admin_matches")}:{" "}
          <b className="text-teal_600">{matchCount === null ? "…" : matchCount}</b>
        </span>
      </div>
      <div className="flex items-center w-full mt-5">
        <CheckBox
          name={t("admin_transportation")}
          inputName={"transport"}
          handleCarData={handleSelectedFilters}
          value={selectedFilters.transport}
        />
        <BsX
          className="text-red-500 text-2xl shrink-0 cursor-pointer"
          onClick={() => {
            handleNullifySelectId("transport");
          }}
        />
      </div>
      <div className="flex gap-2 flex-wrap">
        {selectedFilters.shipmentId && (
          <button
            onClick={async () => {
              try {
                const res = await axiosAdmin.get(`/recShipment/${selectedFilters.shipmentId}`);
                if (res) {
                  successToast(t("success"));
                }
              } catch (err) {
                errorToast(apiError(err, t("error")));
              }
            }}
            className="w-3/12 rounded-lg bg-blue-500 sm:w-full flex items-center justify-center p-1 py-3 text-white mt-5"
          >
            {t("admin_recalculate")}
          </button>
        )}
        {/* The four exports share the same filters; building the query once
            keeps them from drifting apart as filters are added. */}
        <AdminDownloadLink
            filename="users.xlsx"
          href={`${process.env.REACT_APP_API_URL}/api/exportUsers?${exportQuery()}`}
          className="w-3/12 rounded-lg bg-green-400 sm:w-full flex items-center justify-center p-1 py-3 text-white mt-5"
          // target={"_blank"}
        >
          {t("admin_flights_export_csv")}
        </AdminDownloadLink>
        <AdminDownloadLink
            filename="declarantExport.csv"
          href={`${process.env.REACT_APP_API_URL}/api/declarantExport?${exportQuery()}`}
          className="w-3/12 rounded-lg bg-green-400 sm:w-full flex items-center justify-center p-1 py-3 text-white mt-5"
        >
          {t("admin_declarant_export_csv")}
        </AdminDownloadLink>
        {/* XLSX carries real cell formatting, so the identifier columns arrive
            as text instead of Excel guessing at them on import. Slower and
            heavier than the streamed CSV, which is why CSV stays the default. */}
        <AdminDownloadLink
            filename="declarantExport.xlsx"
          href={`${process.env.REACT_APP_API_URL}/api/declarantExport?${exportQuery()}&format=xlsx`}
          className="w-3/12 rounded-lg bg-emerald-600 sm:w-full flex items-center justify-center p-1 py-3 text-white mt-5"
          title="Excel-ის ფაილი — თრექინგები ტექსტად, E+14 გარეშე"
        >
          {t("admin_declarant_export_csv")} (XLSX)
        </AdminDownloadLink>
      </div>

      {/* Every user and their total BOG deposits. */}
      <div className="mt-5 rounded-xl bg-white dark:bg-slate-700 p-4 flex flex-wrap items-end gap-4">
        <div>
          <p className="font-bold text-[#777a83] dark:text-slate-200">{t("admin_users_ledger")}</p>
          <p className="text-xs text-[#989ba5] max-w-2xl leading-snug">{t("admin_users_ledger_hint")}</p>
        </div>
        <label className="flex flex-col gap-1 text-xs text-[#777a83] dark:text-slate-300">
          {t("admin_users_ledger_user")}
          <input
            className="h-9 w-40 px-2 border border-slate-300 dark:border-slate-600 rounded-md
                       bg-white dark:bg-slate-800 text-slate-800 dark:text-white text-sm"
            placeholder="ID (ყველა = ცარიელი)"
            value={ledgerUser}
            onChange={(e) => setLedgerUser(e.target.value.replace(/[^\d]/g, ""))}
          />
        </label>
        <button
          onClick={downloadLedger}
          disabled={ledgerBusy}
          className="rounded-lg bg-indigo-600 disabled:opacity-50 text-white font-bold text-sm px-5 py-2.5"
        >
          {ledgerBusy ? "…" : `${t("admin_users_ledger")} (XLSX)`}
        </button>
        {(dateFrom || dateTo) && (
          <span className="text-xs text-[#989ba5]">{t("admin_users_ledger_dates_note")}</span>
        )}
      </div>
    </div>
  );
};

export default ExportsPage;
